import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Mail, Copy, Check, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetPath, setResetPath] = useState(null);
  const [copied, setCopied] = useState(false);

  const fullLink = resetPath ? window.location.origin + resetPath : null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      let path = res.data?.reset_path || null;
      if (!path && res.data?.reset_url) {
        try { const u = new URL(res.data.reset_url, window.location.origin); path = u.pathname + u.search; } catch { path = res.data.reset_url; }
      }
      setResetPath(path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!fullLink) return;
    try {
      await navigator.clipboard.writeText(fullLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — select and copy the link manually");
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid flex flex-col items-center justify-center p-6">
      <button data-testid="forgot-back-login" onClick={() => navigate("/login")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </button>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-card border border-amber-500/20 rounded-2xl p-8 sm:p-10 shadow-[0_0_60px_rgba(245,158,11,0.06)]">
        <div className="flex items-center gap-3 mb-6">
          <img src="/streamworship-mark.png" alt="StreamWorship" className="h-11 w-11 object-contain" />
          <p className="font-display font-bold text-lg">Stream<span className="text-gold-gradient">Worship</span></p>
        </div>

        {!sent ? (
          <>
            <h1 className="font-display text-2xl font-bold">Reset your password</h1>
            <p className="text-gray-400 text-sm mt-2 mb-6">Enter the email on your account and we'll create a secure reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@church.org" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
                </div>
              </div>
              <button data-testid="forgot-submit" type="submit" disabled={submitting} className="w-full gold-gradient hover:brightness-110 text-black font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {submitting ? "Creating link…" : "Send reset link"}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-amber-400" />
              </span>
              <h1 className="font-display text-2xl font-bold">Check your reset link</h1>
            </div>
            <p data-testid="forgot-confirmation" className="text-gray-400 text-sm mt-2">
              If an account exists for <span className="text-gray-200 font-medium">{email}</span>, a reset link has been created.
            </p>

            {fullLink && (
              <div className="mt-5 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4">
                <p className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold mb-2">Email delivery not enabled yet — use this link</p>
                <div className="flex items-center gap-2">
                  <code data-testid="reset-link" className="flex-1 text-xs text-gray-300 break-all bg-gray-950 rounded-lg px-3 py-2 border border-gray-800">{fullLink}</code>
                  <button data-testid="copy-reset-link" onClick={copyLink} className="shrink-0 h-9 w-9 rounded-lg border border-gray-700 hover:border-amber-500/50 flex items-center justify-center text-gray-300">
                    {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <button data-testid="open-reset-link" onClick={() => navigate(resetPath)} className="mt-3 text-sm text-amber-400 hover:underline">
                  Open reset page →
                </button>
              </div>
            )}

            <p className="text-sm text-gray-400 text-center mt-6">
              <button data-testid="forgot-to-login" onClick={() => navigate("/login")} className="text-amber-400 hover:underline">Back to sign in</button>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
